// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Input from 'components/Input';
import Switch from 'components/Switch';
import { ReactComponent as CheckIcon } from 'assets/svg/check-circle.svg';
import { ReactComponent as BinIcon } from 'assets/svg/bin.svg';
import theme from 'assets/theme/mainTheme';
import { useAuth0 } from '@auth0/auth0-react';
import apiRoutes from 'config/apiRoutes';
import useFetch from 'hooks/useFetch';
import { useDispatch } from 'react-redux';
import {
    dispatchErrorAlertThunk,
    dispatchSuccessAlertThunk,
} from 'redux/actions/thunks/systemAlerts';
import Spinner from 'components/Spinner';
import {
    MetricContainerMPA,
    EnabledSection,
    IconWrapper,
    RangeWrapper,
    RangeInput,
    RangeDivider,
} from './SingleMetric.style';

const TYPES = {
    name: 'name',
    description: 'description',
    goal: 'goal',
    enabled: 'enabled',
    inversed: 'inversed',
    min: 'minimum_value',
    max: 'maximum_value',
};

const SingleMetricMPA = ({
    metricSourceId,
    metric,
    reloadMetrics,
    withoutBorder,
    handleDelete,
}) => {
    const { getAccessTokenSilently, logout } = useAuth0();
    const dispatch = useDispatch();
    const request = useFetch(getAccessTokenSilently, logout);

    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const [name, setName] = useState(metric.name);
    const [description, setDescription] = useState(metric.description);
    const [min, setMin] = useState(metric.minimum_value);
    const [max, setMax] = useState(metric.maximum_value);
    const [goal, setGoal] = useState(metric.goal);
    const [enabled, setEnabled] = useState(metric.enabled);
    const [inversed, setinversed] = useState(metric.inversed);

    const onUpdate = (value, type) => {
        setIsSuccess(false);
        setIsLoading(true);
        request(
            apiRoutes.put.mpaUpdateMetric(metricSourceId, metric.id),
            'PUT',
            (res) => handleSuccess(res, type),
            (err) => handleFailure(err, type),
            {
                body: JSON.stringify({
                    metric: {
                        [type]: value,
                    },
                }),
            },
            { 'Content-Type': 'application/json' }
        );
    };

    const messages = {
        [TYPES.name]: 'Name of metric changed',
        [TYPES.description]: 'Description of metric changed',
        [TYPES.min]: 'Minimum value of metric changed',
        [TYPES.max]: 'Maximum value of metric changed',
        [TYPES.goal]: 'Goal of metric changed',
        [TYPES.enabled]: 'Status of metric changed',
        [TYPES.inversed]: 'Inversed status of metric changed',
    };

    const ids = {
        [TYPES.name]: 'nameEdited',
        [TYPES.description]: 'descriptionEdited',
        [TYPES.min]: 'minEdited',
        [TYPES.max]: 'maxEdited',
        [TYPES.goal]: 'goalEdited',
        [TYPES.enabled]: 'enabledEdited',
        [TYPES.inversed]: 'inversedEdited',
    };
    const handleSuccess = (res, type) => {
        if (!res.error) {
            dispatch(dispatchSuccessAlertThunk(ids[type], messages[type]));
            if (type === TYPES.enabled) setEnabled(!enabled);
            if (type === TYPES.inversed) setinversed(!inversed);
            setIsSuccess(true);
        }
        setIsLoading(false);
    };

    const handleFailure = (err, type) => {
        setIsLoading(false);
        setIsSuccess(false);
        dispatch(dispatchErrorAlertThunk(err, err.error));
        reloadMetrics();
    };

    return (
        <MetricContainerMPA withoutBorder={withoutBorder}>
            <Input
                variant="base"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => {
                    if (name && name !== metric.name && name !== '') {
                        onUpdate(name, TYPES.name);
                    }
                }}
            />
            <Input
                variant="base"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => {
                    if (description) {
                        onUpdate(description, TYPES.description);
                    }
                }}
            />
            <RangeWrapper>
                <RangeInput
                    variant="base"
                    type="number"
                    value={min}
                    onChange={(e) =>
                        e.target.value >= 0 && setMin(e.target.value)
                    }
                    onBlur={() => {
                        if (min && min !== metric.minimum_value) {
                            onUpdate(min, TYPES.min);
                        }
                    }}
                />
                <RangeDivider>to</RangeDivider>
                <RangeInput
                    variant="base"
                    type="number"
                    value={max}
                    onChange={(e) =>
                        e.target.value >= 0 && setMax(e.target.value)
                    }
                    onBlur={() => {
                        if (max && max !== metric.maximum_value) {
                            onUpdate(max, TYPES.max);
                        }
                    }}
                />
            </RangeWrapper>
            <RangeInput
                variant="base"
                type="number"
                value={goal}
                onChange={({ target }) => {
                    if (target.value >= 0) {
                        setGoal(target.value);
                    }
                }}
                onBlur={() => {
                    if (goal && goal !== metric.goal && goal !== '') {
                        onUpdate(goal, TYPES.goal);
                    }
                }}
            />
            <EnabledSection>
                <Switch
                    switchId={`${metric.id}-switch-inversed`}
                    switchOn={() => {
                        onUpdate(true, TYPES.inversed);
                    }}
                    switchOff={() => {
                        onUpdate(false, TYPES.inversed);
                    }}
                    onLabel="Inversed"
                    offLabel="Normal"
                    defaultStatus={inversed}
                    externalStatus={inversed}
                />
                <Switch
                    switchId={`${metric.id}-switch`}
                    switchOn={() => {
                        onUpdate(true, TYPES.enabled);
                    }}
                    switchOff={() => {
                        onUpdate(false, TYPES.enabled);
                    }}
                    defaultStatus={enabled}
                    withConfirmationModal
                    externalStatus={enabled}
                    onConfirmModalDescription="Warning: Changing Metric Status triggers a score recalculation for all affected Organizations. It can take several minutes."
                    offConfirmModalDescription="Warning: Changing Metric Status triggers a score recalculation for all affected Organizations. It can take several minutes."
                />
                {isSuccess ? (
                    <CheckIcon
                        fill={theme.color.checkGreen}
                        style={{
                            marginLeft: 10,
                        }}
                    />
                ) : isLoading ? (
                    <Spinner small />
                ) : (
                    <></>
                )}

                <IconWrapper
                    className="bin"
                    onClick={() => handleDelete(metric.id)}
                >
                    <BinIcon />
                </IconWrapper>
            </EnabledSection>
        </MetricContainerMPA>
    );
};

SingleMetricMPA.propTypes = {
    metricSourceId: PropTypes.string.isRequired,
    reloadMetrics: PropTypes.func.isRequired,
    withoutBorder: PropTypes.bool,
    handleDelete: PropTypes.func.isRequired,
    metric: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        minimum_value: PropTypes.number.isRequired,
        maximum_value: PropTypes.number.isRequired,
        goal: PropTypes.number.isRequired,
        enabled: PropTypes.bool.isRequired,
        inversed: PropTypes.bool.isRequired,
    }).isRequired,
};

SingleMetricMPA.defaultProps = {
    withoutBorder: false,
};

export default SingleMetricMPA;
